/**
 * Unified Import Engine
 * 
 * This module provides a single, unified import function that consolidates
 * the logic from importNotes() and importNotesWithAttachments() into a
 * framework-agnostic, pure function approach.
 * 
 * Key features:
 * - Handles both folder imports (with attachments) and single file imports
 * - Supports both CloudKit and Supabase backends with proper concurrency limits
 * - Processes ImportItem[] from batch builder for unified handling
 * - Bounded concurrency to prevent memory issues and API rate limits
 * - Comprehensive error collection and progress tracking
 * - Pure function design for better testability
 */

import { v4 as uuid } from 'uuid'
import { NoteType, removeExtension, type Note } from '../note'
import { resolveImportLocation } from '../folderOperations'
import { detectCalendarConflict, detectProjectConflictWithMap } from './conflictDetection'
import { createFolderScopedFilenameMap, extractFolderPath, findFolderUuid } from '../folderOperations'

import {
  createEmptyImportStats,
  type ImportResult,
  type ImportError,
  type ImportWarning,
  type ImportHint,

  type ImportItem,
  type ImportNote,
  type ImportConflictChoice,
  type ImportStats,
} from './core'
import { assertNever } from './types'
import { AuthType } from '../User'
import type { AttachmentStrategy, AttachmentUploadResult, AttachmentUploadFailure } from './attachmentStrategies'
import type { AttachmentFailure } from './log'

/**
 * Helper function to safely extract recordName from a note
 * Throws a descriptive error if recordName is missing to prevent data corruption
 */
function assertRecordName(note: Note, filename: string): string {
  if (!note.recordName) {
    throw new Error(`Existing note missing recordName: ${filename}`)
  }
  return note.recordName
}

/**
 * Helper function to process attachment upload failures
 * Consolidates the repeated logic for handling attachment failures across different scenarios
 */
function processAttachmentFailures(
  failures: AttachmentUploadFailure[],
  notePath: string,
  warnings: ImportWarning[],
  attachmentsFailed: AttachmentFailure[]
): void {
  for (const failure of failures) {
    warnings.push({
      path: notePath,
      message: `Attachment '${failure.filename}' failed: ${failure.error}`,
    })
    attachmentsFailed.push({
      notePath,
      filename: failure.filename,
      error: failure.error,
    })
  }
}

/**
 * Configuration options for the unified import engine
 */
export type EngineOptions = {
  /** Attachment strategy for backend-specific attachment handling (undefined for no attachments) */
  attachmentStrategy: AttachmentStrategy | undefined
  /** Progress callback for UI updates */
  onProgress?: (done: number, total: number) => void
  /** Conflict resolution callback */
  onConflict?: (filename: string, isCalendarFile: boolean) => Promise<ImportConflictChoice>
  /** Maximum attachment file size in bytes (50MB default) */
  maxAttachmentBytes?: number
  /** Authentication type for backend-specific processing */
  authType: AuthType

  /** REQUIRED: Live cache getter for project notes - reads from TanStack Query cache on-demand */
  getProjectNotesMap: () => Map<string, Note>
  /** REQUIRED: Live cache getter for calendar notes grouped by date - reads from TanStack Query cache on-demand */
  getCalendarNotesByDate: () => Map<string, Note[]>

  /** Map of folder paths to their record names */
  folderIdMap: Map<string, string>
  /** Target root folder for import (empty string for root) */
  targetRootFolder: string
  /** Create note function for direct note creation */
  createNote: (parameters: {
    recordName: string
    noteType: NoteType
    filename: string
    content: string
    parent: string | null
    isDir?: boolean
  }) => Promise<Note>
  /** Save note function for updating existing notes */
  saveNote: (parameters: {
    content: string
    attachments?: string[]
    recordName: string
    filename: string
    parent: string | null
    noteType: NoteType
    modificationDate: Date
  }) => Promise<Note>
}

/**
 * Unified import engine that processes ImportItem[] sequentially
 * 
 * This function replaces both importNotes() and importNotesWithAttachments()
 * with a single, unified approach that handles:
 * - Folder creation (iterative, not recursive)
 * - Note creation with optional attachments
 * - Sequential processing to prevent CloudKit CAS Op-Lock conflicts
 * - Comprehensive error collection
 * - Memory-efficient processing
 */
export async function runImport(
  items: readonly ImportItem[],
  options: EngineOptions
): Promise<ImportResult> {
  const stats = createEmptyImportStats()
  const errors: ImportError[] = []
  const warnings: ImportWarning[] = []
  const hints: ImportHint[] = []
  const attachmentsFailed: AttachmentFailure[] = []

  // Separate folders and notes for processing
  const folders: string[] = []
  const notes: ImportNote[] = []

  for (const item of items) {
    switch (item.kind) {
      case 'folder': {
        folders.push(item.path)
        break
      }
      case 'note': {
        notes.push(item.note)
        break
      }
      default: {
        assertNever(item)
      }
    }
  }

  // Progress tracking
  const totalOperations = folders.length + notes.length
  let completedOperations = 0

  const fireProgress = () => {
    if (options.onProgress) {
      completedOperations++
      options.onProgress(completedOperations, totalOperations)
    }
  }

  // 1. Create folders iteratively (not recursively to avoid stack overflow)
  await createFoldersIteratively(folders, options, stats, warnings, fireProgress)

  // 2. Process notes sequentially to prevent CloudKit CAS Op-Lock conflicts
  // Cache folder maps to avoid rebuilding for notes in the same folder
  const folderMapCache = new Map<string, Map<string, Note[]>>()

  for (const note of notes) {
    try {
      await processNote(note, options, stats, errors, warnings, hints, attachmentsFailed, folderMapCache)
    } finally {
      fireProgress()
    }
  }

  return {
    stats,
    errors,
    warnings,
    hints,
    attachmentsFailed,
  }
}

/**
 * Create folders iteratively to avoid stack overflow with deep hierarchies
 */
async function createFoldersIteratively(
  folders: string[],
  options: EngineOptions,
  stats: ImportStats,
  warnings: ImportWarning[],
  fireProgress: () => void
): Promise<void> {
  // Sort folders by depth to ensure parent folders are created first
  const sortedFolders = [...folders].sort((a, b) => {
    const depthA = a.split('/').length
    const depthB = b.split('/').length
    return depthA - depthB
  })

  for (const folderPath of sortedFolders) {
    try {
      // Check if folder already exists in folderIdMap
      if (options.folderIdMap.has(folderPath)) {
        stats.foldersReused++
        continue
      }

      // Check for existing folder in DB first
      const projectNotes = options.getProjectNotesMap()

      if (options.authType === AuthType.SUPABASE) {
        // Supabase: UUID-based folder hierarchy
        const existingUuid = findFolderUuid(projectNotes, folderPath)
        if (existingUuid) {
          // Reuse existing folder
          options.folderIdMap.set(folderPath, existingUuid)
          stats.foldersReused++
          continue
        }

        // Create new Supabase folder with parent UUID
        const folderName = folderPath.split('/').pop() ?? 'Untitled'
        const parentPath = folderPath.split('/').slice(0, -1).join('/')

        // Determine parent folder ID
        // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
        let parentId: string | null = null
        if (parentPath && options.folderIdMap.has(parentPath)) {
          // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
          parentId = options.folderIdMap.get(parentPath) ?? null
        } else if (options.targetRootFolder) {
          // Use target root folder as parent for top-level folders
          // First check folderIdMap (which contains the newly created import folder)
          if (options.folderIdMap.has(options.targetRootFolder)) {
            // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
            parentId = options.folderIdMap.get(options.targetRootFolder) ?? null
          } else {
            // Fallback to searching in project notes (shouldn't happen for import folder)
            const rootNote = [...projectNotes.values()].find(
              note => note.isFolder && note.filename === options.targetRootFolder && !note.parent
            )
            // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
            parentId = rootNote?.recordName ?? null
          }
        }

        // Create the folder note
        const folderNote = await options.createNote({
          recordName: uuid(),
          noteType: NoteType.PROJECT_NOTE,
          filename: folderName,
          content: '',
          parent: parentId,
          isDir: true,
        })

        // Add to folderIdMap for future reference
        if (folderNote.recordName) {
          options.folderIdMap.set(folderPath, folderNote.recordName)
          stats.foldersCreated++
        } else {
          throw new Error(`Created folder note missing recordName: ${folderPath}`)
        }
      } else {
        // CloudKit/CLOUDKIT_SUPABASE: Path-based folder hierarchy
        // For CloudKit, if targetRootFolder is specified, prepend it to the folder path
        const fullFolderPath = options.targetRootFolder
          ? `${options.targetRootFolder}/${folderPath}`
          : folderPath

        // Check if folder already exists with the full path as filename
        const existingFolder = [...projectNotes.values()].find(
          note => note.isFolder && note.filename === fullFolderPath
        )

        if (existingFolder) {
          // Reuse existing folder
          if (existingFolder.recordName) {
            // Store mapping with original path for later reference
            options.folderIdMap.set(folderPath, existingFolder.recordName)
          }
          stats.foldersReused++
          continue
        }

        // Create new CloudKit folder with full path as filename
        // For CloudKit, parent is always null (path-based hierarchy)
        const folderNote = await options.createNote({
          recordName: uuid(),
          noteType: NoteType.PROJECT_NOTE,
          filename: fullFolderPath, // Full path including target folder for CloudKit
          content: '',
          // eslint-disable-next-line unicorn/no-null -- CloudKit uses null for parent
          parent: null,
          isDir: true,
        })

        // Add to folderIdMap for future reference (using original path as key)
        if (folderNote.recordName) {
          options.folderIdMap.set(folderPath, folderNote.recordName)
          stats.foldersCreated++
        } else {
          throw new Error(`Created folder note missing recordName: ${fullFolderPath}`)
        }
      }
    } catch (error) {
      // Track folder creation failures for user visibility
      stats.errors++
      warnings.push({
        path: folderPath,
        message: error instanceof Error ? error.message : 'Folder creation failed'
      })
    } finally {
      fireProgress()
    }
  }
}

/**
 * Process a single note with attachment support
 */
async function processNote(
  note: ImportNote,
  options: EngineOptions,
  stats: ImportStats,
  errors: ImportError[],
  warnings: ImportWarning[],
  _hints: ImportHint[],
  attachmentsFailed: AttachmentFailure[],
  folderMapCache: Map<string, Map<string, Note[]>>
): Promise<void> {
  try {
    // Filter out oversized attachments if present
    let validAttachments = note.attachments
    if (note.attachments.length > 0 && options.maxAttachmentBytes) {
      validAttachments = []
      for (const attachment of note.attachments) {
        if (attachment.size > options.maxAttachmentBytes) {
          const sizeMB = String(Math.round(attachment.size / 1024 / 1024))
          const limitMB = String(Math.round(options.maxAttachmentBytes / 1024 / 1024))
          warnings.push({
            path: note.path,
            message: `Attachment '${attachment.name}' exceeds ${limitMB}MB limit (${sizeMB}MB) and will be skipped`,
          })
        } else {
          validAttachments.push(attachment)
        }
      }
    }

    // Determine note type and target location
    const noteType = note.isCalendar ? NoteType.CALENDAR_NOTE : NoteType.PROJECT_NOTE
    const basename = note.path.split('/').pop() ?? 'Untitled'

    // Handle conflict detection
    if (noteType === NoteType.CALENDAR_NOTE) {
      // Calendar notes: check against calendar notes map directly (no folder map needed)
      const calendarByDate = options.getCalendarNotesByDate()
      const conflict = detectCalendarConflict(basename, calendarByDate)

      if (conflict.hasConflict && options.onConflict) {
        const choice = await options.onConflict(basename, true)
        if (choice === 'skip') {
          stats.skipped++
          return
        }

        // Read file content only when needed (not skipped)
        const content = await note.file.text()

        if (choice === 'merge' && conflict.existingNote) {
          // Merge calendar notes: new content + blank line + existing content
          const merged = `${content}${content && (conflict.existingNote.content ?? '') ? '\n\n' : ''}${conflict.existingNote.content ?? ''}`
          await options.saveNote({
            content: merged,
            recordName: assertRecordName(conflict.existingNote, basename),
            filename: conflict.existingNote.filename,
            parent: conflict.existingNote.parent,
            noteType: conflict.existingNote.noteType,
            modificationDate: new Date(),
          })
          stats.overwritten++
          return
        }

        if (choice === 'overwrite' && conflict.existingNote) {
          // Overwrite existing calendar note
          await options.saveNote({
            content,
            recordName: assertRecordName(conflict.existingNote, basename),
            filename: conflict.existingNote.filename,
            parent: conflict.existingNote.parent,
            noteType: conflict.existingNote.noteType,
            modificationDate: new Date(),
          })
          stats.overwritten++
          return
        }

        if (choice === 'duplicate') {
          // Calendar notes cannot be duplicated (unique per date)
          // Fall through to create new note
        }
      }
    } else {
      // Project notes: check against project notes map with caching
      const folderPath = extractFolderPath(note.path)

      // Check cache first to avoid rebuilding folder map for notes in same folder
      let folderFilenameMap = folderMapCache.get(folderPath)
      if (!folderFilenameMap) {
        const projectNotes = options.getProjectNotesMap()
        folderFilenameMap = createFolderScopedFilenameMap(
          projectNotes,
          folderPath,
          options.authType,
          options.folderIdMap
        )
        folderMapCache.set(folderPath, folderFilenameMap)
      }

      const conflict = detectProjectConflictWithMap(basename, folderFilenameMap)

      if (conflict.hasConflict && options.onConflict) {
        const choice = await options.onConflict(basename, false)
        if (choice === 'skip') {
          stats.skipped++
          return
        }

        // Read file content only when needed (not skipped)
        const content = await note.file.text()

        if (choice === 'overwrite' && conflict.existingNote) {
          // Overwrite existing project note
          if (options.attachmentStrategy && validAttachments.length > 0) {
            // Use attachment strategy for overwrite with attachments
            const attachmentNames = validAttachments.map(f => f.name)
            await options.attachmentStrategy.save(conflict.existingNote, content, attachmentNames)

            // Handle two-phase upload for Supabase
            if (options.attachmentStrategy.uploadAttachmentsFirstPhase) {
              try {
                const uploadResult: AttachmentUploadResult = await options.attachmentStrategy.uploadAttachmentsFirstPhase(conflict.existingNote, validAttachments)

                // Process attachment failures using helper function
                processAttachmentFailures(uploadResult.failures, note.path, warnings, attachmentsFailed)
              } catch (error) {
                warnings.push({
                  path: note.path,
                  message: `Attachment upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                })
              }
            }
          } else {
            // Direct overwrite without attachments
            await options.saveNote({
              content,
              recordName: assertRecordName(conflict.existingNote, basename),
              filename: conflict.existingNote.filename,
              parent: conflict.existingNote.parent,
              noteType: conflict.existingNote.noteType,
              modificationDate: new Date(),
            })
          }
          stats.overwritten++
          return
        }

        if (choice === 'duplicate' && conflict.existingNote) {
          // Create duplicate note preserving the original subfolder structure
          // Use resolveImportLocation to properly handle path-based (CloudKit) and UUID-based (Supabase) backends
          const { filename: resolvedFilename, parent: resolvedParent } = resolveImportLocation(
            note.path,
            options.authType,
            options.folderIdMap,
            options.targetRootFolder,
            options.getProjectNotesMap()
          )

          if (options.attachmentStrategy && validAttachments.length > 0) {
            // Use resolved location for duplicate with attachments
            const location = {
              filename: removeExtension(resolvedFilename),
              parent: resolvedParent,
            }

            // Use attachment strategy for duplicate with attachments
            const duplicateNote = await options.attachmentStrategy.create({ ...note, attachments: validAttachments }, location)

            // Handle two-phase upload for Supabase
            if (options.attachmentStrategy.uploadAttachmentsFirstPhase) {
              try {
                const uploadResult: AttachmentUploadResult = await options.attachmentStrategy.uploadAttachmentsFirstPhase(duplicateNote, validAttachments)

                // Process attachment failures using helper function
                processAttachmentFailures(uploadResult.failures, note.path, warnings, attachmentsFailed)
              } catch (error) {
                warnings.push({
                  path: note.path,
                  message: `Attachment upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
                })
              }
            }
          } else {
            // Direct duplicate creation without attachments
            await options.createNote({
              recordName: uuid(),
              noteType: NoteType.PROJECT_NOTE,
              parent: resolvedParent,
              filename: removeExtension(resolvedFilename),
              content,
            })
          }
          stats.duplicated++
          return
        }
      }
    }

    // Read file content for new note creation (no conflict or duplicate case)
    const content = await note.file.text()

    // Create note using attachment strategy or direct creation
    if (options.attachmentStrategy && validAttachments.length > 0) {
      // Resolve location for notes with attachments (same logic as notes without attachments)
      const location = noteType === NoteType.CALENDAR_NOTE
        ? {
            filename: removeExtension(basename),
            // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
            parent: null,
          }
        : resolveImportLocation(
            note.path,
            options.authType,
            options.folderIdMap,
            options.targetRootFolder,
            options.getProjectNotesMap()
          )

      // Use attachment strategy for notes with attachments
      const createdNote = await options.attachmentStrategy.create({ ...note, attachments: validAttachments }, location)

      // Handle two-phase upload for Supabase
      if (options.attachmentStrategy.uploadAttachmentsFirstPhase) {
        try {
          const uploadResult: AttachmentUploadResult = await options.attachmentStrategy.uploadAttachmentsFirstPhase(createdNote, validAttachments)

          // Process attachment failures using helper function
          processAttachmentFailures(uploadResult.failures, note.path, warnings, attachmentsFailed)

          // Log summary if there were failures
          if (uploadResult.failed > 0) {
            warnings.push({
              path: note.path,
              message: `Attachment upload: ${String(uploadResult.failed)} of ${String(uploadResult.uploaded + uploadResult.failed)} failed (${uploadResult.failures.map(f => f.filename).join(', ')})`
            })
          }
        } catch (error) {
          // Fallback for unexpected errors
          warnings.push({
            path: note.path,
            message: `Attachment upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
          })
        }
      }

      stats.created++
    } else {
      // Direct note creation without attachments
      if (noteType === NoteType.CALENDAR_NOTE) {
        // Calendar notes always go to root
        await options.createNote({
          recordName: uuid(),
          noteType,
          // eslint-disable-next-line unicorn/no-null -- Database requires null for parent property
          parent: null,
          filename: removeExtension(basename), // Remove extension as createNote expects it
          content,
        })
      } else {
        // Project notes use backend-aware resolution
        const { filename, parent } = resolveImportLocation(
          note.path,
          options.authType,
          options.folderIdMap,
          options.targetRootFolder,
          options.getProjectNotesMap()
        )

        await options.createNote({
          recordName: uuid(),
          noteType,
          parent,
          filename: removeExtension(filename), // Remove extension as createNote expects it
          content,
        })
      }

      stats.created++
    }

  } catch (error) {
    stats.errors++
    errors.push({
      path: note.path,
      message: `Import failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
    })
  }
}
